/**
 * Plan gate hook + store. Catches PLAN_LIMIT_EXCEEDED and
 * INSUFFICIENT_CREDITS errors from API calls and opens the
 * upgrade modal with the right context.
 *
 * Usage in a component:
 *   const { showUpgrade } = usePlanGate();
 *
 * Or to manually trigger:
 *   showUpgrade({ limit: "videos_per_month", currentPlan: "starter", requiredPlan: "pro" });
 *
 * The <UpgradeModal /> rendered in App.tsx reads from the store.
 */
import { create } from "zustand";
import type { ApiError } from "@/api/client.ts";

export type PlanLimitType =
  | "videos_per_month"
  | "regens_per_segment"
  | "connected_channels"
  | "active_series"
  | "team_seats"
  | "youtube_publish"
  | "scheduled_publish"
  | "no_active_plan"
  | "credits";

export interface PlanLimitDetails {
  limit: PlanLimitType;
  currentPlan: string | null;
  currentLimit?: number;
  requiredPlan?: string | null;
}

interface PlanGateState {
  open: boolean;
  details: PlanLimitDetails | null;
  message: string | null;
  showUpgrade: (details: PlanLimitDetails, message?: string) => void;
  close: () => void;
}

export const usePlanGateStore = create<PlanGateState>((set) => ({
  open: false,
  details: null,
  message: null,
  showUpgrade: (details, message) =>
    set({ open: true, details, message: message ?? null }),
  close: () => set({ open: false, details: null, message: null }),
}));

/** Convenience hook that returns showUpgrade + close. */
export function usePlanGate() {
  const showUpgrade = usePlanGateStore((s) => s.showUpgrade);
  const close = usePlanGateStore((s) => s.close);
  return { showUpgrade, close };
}

/**
 * Returns true if the error is a plan-limit error that should
 * trigger the upgrade modal. Call from mutation onError callbacks
 * or from the global mutation error handler.
 */
export function isPlanLimitError(error: unknown): error is ApiError & {
  details: PlanLimitDetails;
} {
  const e = error as ApiError | undefined;
  if (!e || typeof e !== "object") return false;
  return (
    e.status === 402 &&
    (e.code === "PLAN_LIMIT_EXCEEDED" || e.code === "INSUFFICIENT_CREDITS") &&
    !!e.details &&
    typeof (e.details as PlanLimitDetails).limit === "string"
  );
}

/**
 * Call from the global QueryClient onMutationError or from
 * a per-mutation onError. If the error is a plan-limit error,
 * opens the upgrade modal and returns true. Otherwise returns false.
 */
export function handlePlanLimitError(error: unknown): boolean {
  if (!isPlanLimitError(error)) return false;
  usePlanGateStore
    .getState()
    .showUpgrade(error.details as PlanLimitDetails, error.message);
  return true;
}
